import React, { useMemo } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from 'recharts';
import { alpha, Box, useTheme } from '@mui/material';
import { getFormattedDate } from 'helpers/dayJsFormats';
import { formatCurrency } from 'helpers/formatters';
import { CampaignTypes } from 'api';
import convertData from './convertData';

interface Props {
  data: CampaignTypes.PerformanceChartData;
}

const Chart = (props: Props): JSX.Element | null => {
  const { data } = props;
  const theme = useTheme();

  const chartData = useMemo(() => convertData(data), [data]);

  return (
    <Box width="100%" height="450px">
      <ResponsiveContainer>
        <AreaChart
          data={chartData}
          margin={{
            top: 16,
            right: 16,
            left: 16,
            bottom: 0,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="key" tickFormatter={getFormattedDate} />
          <YAxis tickFormatter={(v) => formatCurrency(v, 0)} />
          <Tooltip labelFormatter={(label) => getFormattedDate(label)} />
          <Area
            name="Forecast"
            type="linear"
            dot={{
              stroke: theme.palette.warning.main,
              fill: theme.palette.common.white,
              fillOpacity: 1,
              strokeWidth: 2,
              r: 4,
            }}
            dataKey="forecast"
            stackId="1"
            strokeWidth={2}
            stroke={theme.palette.warning.main}
            fill={alpha(theme.palette.warning.main, 0.1)}
          />
          <Area
            name="Actual"
            type="linear"
            dot={{
              stroke: theme.palette.primary.main,
              fill: theme.palette.common.white,
              fillOpacity: 1,
              strokeWidth: 2,
              r: 4,
            }}
            dataKey="actual"
            stackId="3"
            strokeWidth={2}
            stroke={theme.palette.primary.main}
            fill={alpha(theme.palette.primary.main, 0.1)}
          />
          <Area
            name="Potential"
            type="linear"
            dot={{
              stroke: theme.palette.secondary.main,
              fill: theme.palette.common.white,
              fillOpacity: 1,
              strokeWidth: 2,
              r: 4,
            }}
            dataKey="potential"
            stackId="2"
            strokeWidth={2}
            stroke={theme.palette.secondary.main}
            fill={alpha(theme.palette.secondary.main, 0.1)}
          />
          <Legend iconType="circle" />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default Chart;
